import { Injectable } from '@angular/core';
import { PrFormService } from '@app/components/pr-form/pr-form.service';
import { PrTableColumn } from '@app/components/pr-table/pr-table.component';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, Subject, tap } from 'rxjs';

export type CashOrCredit = 'CASH' | 'CREDIT';

export interface SimulationPack {
  id?: string;
  index?: number;
  name?: string;
  description?: string;
  buttonText?: string;
  suitable?: boolean;
  recommended?: boolean;
  allocation?: Allocation[];
  meanExpectedRateDVM?: number;
  minimumSubscriptionAmount?: number;
  annualizedPerformance?: number;
  volatility?: number;
  riskScore?: number;
  totalCapitalization?: number;
  img?: string;
  imageUrl?: string;
  organizationId?: number;
}

/**
 * Indique si la liste contient au moins un vrai pack, le choix manuel n'en étant pas un.
 */
export function containsPack(packs: SimulationPack[] | undefined): boolean {
  return !!packs?.some(pack => pack.id != null);
}

export interface Allocation {
  name?: string;
  partPrice: number;
  omniprodId?: string;
  scpi8Id?: string;
  shareCount?: number;
  investedAmount?: number;
  allocationPercentage?: string;
  totalInvestedAmount?: number;
}

export interface PackService {
  type: string;
  customPack: SimulationPack;
  packForm: any;
  template: any;
  getPacks(productId?: number, cashOrCredit?: CashOrCredit): Observable<any>;
}

@Injectable()
export class SimulationPackService {
  constructor(private prFormService: PrFormService) {}

  template?: any;
  customPack: SimulationPack = {};
  packs: SimulationPack[] = [this.customPack];
  packsChangedSubject: BehaviorSubject<SimulationPack[]> = new BehaviorSubject(
    this.packs,
  );
  packForm?: any;
  type?: any;

  set selectedPack(pack: SimulationPack) {
    this._selectedPack = pack;
    this.onSelectedPackChange().next(pack);
  }

  get selectedPack(): SimulationPack {
    return this._selectedPack;
  }
  _selectedPack?: SimulationPack;
  selectedPackSubject: Subject<SimulationPack> = new Subject();

  allocationFormSubject: Subject<any> = new Subject();

  tableDefinitionColumn: PrTableColumn[];
  tableSubject: Subject<void> = new Subject<void>();
  tableData: any = [];
  productId: number;
  cashOrCredit: CashOrCredit;
  packService: PackService;

  selectConfig(packService: PackService) {
    this.packService = packService;
    this.type = packService.type;
    this.customPack = packService.customPack;
    this.template = packService.template;
    this.packForm = packService.packForm;
  }

  /**
   * Charge les packs du mode de financement courant ; une réponse arrivée après un changement de mode est rendue à
   * l'appelant sans être affichée.
   */
  getPacks() {
    const requestedCashOrCredit = this.cashOrCredit;
    return this.packService
      .getPacks(this.productId, requestedCashOrCredit)
      .pipe(
        tap(packs => {
          if (requestedCashOrCredit === this.cashOrCredit) this.setPacks(packs);
        }),
      );
  }

  setPacks(packs) {
    this.packs = _.concat(this.customPack, packs);
    this.packsChangedSubject.next(this.packs);
  }

  findById(id: string) {
    let pack;
    if (id) {
      pack = _.find(this.packs, { id: id });
    }
    if (!pack) {
      pack = this.getCustomPack();
    }
    return pack;
  }

  selectPack(pack: SimulationPack) {
    this.selectedPack = pack;

    if (this.selectedPack == this.getCustomPack()) {
      this.setManualPack({ ...this.getCustomPack(), allocation: [] });
      this.setTableData([]);
    }

    this.selectedPackSubject.next(this.selectedPack);
  }

  onSelectedPackChange() {
    return this.selectedPackSubject;
  }

  startAllocationForm(options?, customAllocation?: Allocation[]) {
    this.prFormService.trigger(
      this.packForm,
      customAllocation ? customAllocation : this.selectedPack.allocation,
      allocation => this.onAllocationFormDone(allocation),
      options,
    );
  }

  onAllocationFormDone(allocation: Allocation[]) {
    this.selectedPack = { ...this.selectedPack, allocation: allocation };
    return this.allocationFormSubject.next(this.selectedPack);
  }

  getCustomPack() {
    return this.packs.find(pack => !pack.index);
  }

  setManualPack(pack: SimulationPack) {
    this.customPack = { ...this.getCustomPack(), ...pack };
  }

  setTableData(data) {
    this.tableData = data;
    this.refreshTable();
  }

  refreshTable() {
    this.tableSubject.next();
  }
}
